import { useCallback, useRef, useState } from "react";

type UseHoverProps = {
  onEnter?: () => void;
  onLeave?: () => void;
};

export type UseHoverEventHandlers = {
  onMouseOver: (ev: React.MouseEvent) => void;
  onMouseLeave: (ev: React.MouseEvent) => void;
};

export const useHover = ({ onEnter, onLeave }: UseHoverProps = {}): {
  eventHandlers: UseHoverEventHandlers;
  hover: boolean;
  everHovered: boolean;
  anchorEl: Element | null;
  forceUnHover: () => void;
} => {
  const [hover, setHover] = useState(false);
  const [everHovered, setEverHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const mouseOverStart = useRef<Date | null>(null);
  const handleMouseOver = useCallback(
    (event: React.MouseEvent) => {
      setHover((currentValue) => {
        // Sometimes the event is retriggered by moving the mouse inside the
        // hovered element, if the hovered element contains children which can
        // take the mouse focus. We only want to trigger `onEnter` the first time
        // the mouse enters.
        if (!currentValue) {
          onEnter?.();
        }
        return true;
      });
      setEverHovered(true);
      setAnchorEl(event.currentTarget);
      mouseOverStart.current = new Date();
    },
    [onEnter],
  );
  const handleMouseLeave = useCallback(() => {
    setHover((currentValue) => {
      if (currentValue) {
        onLeave?.();
      }
      return false;
    });
    setAnchorEl(null);
    if (mouseOverStart.current) {
      mouseOverStart.current = null;
    }
  }, [onLeave]);
  /**
   * Simulate un-hovering, making this effectively not-hovered until the mouse
   * moves away and reenters. Used to make hover-menus close.
   */
  const forceUnHover = useCallback(() => {
    setHover((currentValue) => {
      if (currentValue) {
        onLeave?.();
      }
      return false;
    });
    setAnchorEl(null);
  }, [onLeave]);
  return {
    eventHandlers: {
      onMouseOver: handleMouseOver,
      onMouseLeave: handleMouseLeave,
    },
    hover,
    everHovered,
    anchorEl,
    forceUnHover,
  };
};

export function roundRectOutwards(rect: DOMRect | null) {
  if (!rect) return rect;
  return new DOMRect(
    Math.floor(rect.left),
    Math.floor(rect.top),
    Math.ceil(rect.width),
    Math.ceil(rect.height),
  );
}

export function getDOMRectContainingPoint(
  rects: DOMRectList,
  x: number,
  y: number,
): DOMRect | null {
  for (let i = 0; i < rects.length; i++) {
    const rect = roundRectOutwards(rects.item(i));
    if (
      rect &&
      x >= rect.left &&
      x <= rect.right &&
      y >= rect.top &&
      y <= rect.bottom
    ) {
      return rect;
    }
  }
  return null;
}

export function translateDomRect(
  rect: DOMRect,
  offsetX: number,
  offsetY: number,
): DOMRect {
  return new DOMRect(
    rect.left + offsetX,
    rect.top + offsetY,
    rect.width,
    rect.height,
  );
}
